import { HttpClient } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { UtilsService } from '../../services/utils/utils.service';
import { environment } from '@env';
import * as mammoth from 'mammoth';
import * as XLSX from 'xlsx';

@Component({
    selector: 'app-attachment-file-view',
    templateUrl: './attachment-file-view.component.html',
    styleUrls: ['./attachment-file-view.component.scss'],
    standalone: false,
})
export class AttachmentFileViewComponent implements OnInit, OnDestroy {
    @Input() fileUrl!: string;

    fileType = '';
    safeUrl: any;
    textContent: any;

    constructor(private http: HttpClient, private sanitizer: DomSanitizer, private utils: UtilsService) {}

    private processPDF = () => {
        this.utils.showLoading();
        const url = environment.taxilla_api + '/process/v1/files?fileUrl=' + encodeURIComponent(this.fileUrl);
        this.http.get(url, { responseType: 'blob' }).subscribe(blob => {
            try {
                this.fileType = 'application/pdf';
                this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(blob));
                this.utils.hideLoading();
            } catch {
                this.textContent = 'Error: Invalid PDF format';
            }
            this.utils.hideLoading();
        });
    };

    private processText = () => {
        this.utils.showLoading();
        const url = environment.taxilla_api + '/process/v1/files?fileUrl=' + encodeURIComponent(this.fileUrl);
        this.http.get(url, { responseType: 'text' }).subscribe(content => {
            try {
                this.fileType = 'text/html';
                this.textContent = this.sanitizer.bypassSecurityTrustHtml(content);
                this.utils.hideLoading();
            } catch {
                this.textContent = 'Error: Invalid text format';
            }
            this.utils.hideLoading();
        });
    };

    private processImage = () => {
        this.utils.showLoading();
        const url = environment.taxilla_api + '/process/v1/files?fileUrl=' + encodeURIComponent(this.fileUrl);
        this.http.get(url, { responseType: 'blob' }).subscribe(blob => {
            try {
                this.fileType = 'image';
                this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(blob));
                this.utils.hideLoading();
            } catch {
                this.textContent = 'Error: Invalid image format';
            }
            this.utils.hideLoading();
        });
    };

    private processJSON = () => {
        this.utils.showLoading();
        const url = environment.taxilla_api + '/process/v1/files?fileUrl=' + encodeURIComponent(this.fileUrl);
        this.http.get(url, { responseType: 'text' }).subscribe(content => {
            try {
                const jsonData = JSON.parse(content);
                this.fileType = 'application/json';
                this.textContent = JSON.stringify(jsonData, null, 2);
            } catch {
                this.textContent = 'Error: Invalid JSON format';
            }
            this.utils.hideLoading();
        });
    };

    private processXML = () => {
        this.utils.showLoading();
        const url = environment.taxilla_api + '/process/v1/files?fileUrl=' + encodeURIComponent(this.fileUrl);
        this.http.get(url, { responseType: 'text' }).subscribe(content => {
            try {
                this.fileType = 'application/xml';
                this.textContent = this.formatXml(content);
            } catch {
                this.textContent = 'Error: Could not process XML file';
            }
            this.utils.hideLoading();
        });
    };

    private formatXml(xml: string): string {
        const escapedXml = xml.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const formatted = escapedXml
            .replace(/(&lt;\/?[\w:-]+)([^>]*?)(\/?&gt;)/g, (_match, tagStart, content, tagEnd) => {
                const formattedContent = content.replace(
                    /([\w:-]+)=(["'])(.*?)\2/g,
                    (_, attr, quote, value) =>
                        ` <span class="text-[#9cdcfe]">${attr}</span>=${quote}<span class="text-[#ce9178]">${value}</span>${quote}`
                );
                return `<span class="text-[#569cd6]">${tagStart}</span>${formattedContent}<span class="text-[#569cd6]">${tagEnd}</span>`;
            })
            .replace(/(&lt;\?[^>]*\?&gt;)/g, '<span class="text-[#808080]">$1</span>');

        return `<pre class="text-[#d4d4d4] font-mono whitespace-pre-wrap break-words">${formatted}</pre>`;
    }

    private processExcel = () => {
        this.utils.showLoading();
        const url = environment.taxilla_api + '/process/v1/files?fileUrl=' + encodeURIComponent(this.fileUrl);
        this.http.get(url, { responseType: 'arraybuffer' }).subscribe(
            arrayBuffer => {
                try {
                    const data = new Uint8Array(arrayBuffer);
                    const workbook = XLSX.read(data, { type: 'array' });

                    // Convert all sheets to HTML tables
                    let htmlContent = '';
                    workbook.SheetNames.forEach(sheetName => {
                        const worksheet = workbook.Sheets[sheetName];
                        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

                        if (jsonData.length > 0) {
                            htmlContent += `<div class="mb-8">`;
                            htmlContent += `<h3 class="text-lg font-semibold mb-2">${sheetName}</h3>`;
                            htmlContent += `<div class="overflow-x-auto">`;
                            htmlContent += `<table class="min-w-full bg-white border border-gray-200">`;

                            // Add header row if there are multiple rows
                            if (jsonData.length > 1) {
                                const headerRow: any = jsonData[0] || [];
                                htmlContent += `<thead><tr>`;
                                headerRow.forEach((cell: any) => {
                                    htmlContent += `<th class="bg-gray-100 border-b border-gray-200 px-4 py-2 text-left">${cell}</th>`;
                                });
                                htmlContent += `</tr></thead>`;
                            }

                            // Add data rows
                            htmlContent += `<tbody>`;
                            const startRow = jsonData.length > 1 ? 1 : 0; // Skip header row if it exists
                            for (let i = startRow; i < jsonData.length; i++) {
                                const row = jsonData[i] as any[];
                                htmlContent += `<tr class="hover:bg-gray-50">`;
                                row.forEach((cell: any) => {
                                    htmlContent += `<td class="border-b border-gray-200 px-4 py-2">${cell}</td>`;
                                });
                                htmlContent += `</tr>`;
                            }
                            htmlContent += `</tbody>`;
                            htmlContent += `</table>`;
                            htmlContent += `</div>`; // Close overflow-x-auto
                            htmlContent += `</div>`; // Close mb-8
                        }
                    });

                    this.fileType = 'application/vnd.ms-excel';
                    this.textContent = this.sanitizer.bypassSecurityTrustHtml(htmlContent || 'No data found in the Excel file');
                } catch {
                    this.textContent = 'Error: Could not process Excel file';
                }
                this.utils.hideLoading();
            },
            _error => {
                this.textContent = 'Error: Could not load Excel file';
                this.utils.hideLoading();
            }
        );
    };

    private processDocx = () => {
        this.utils.showLoading();
        const url = environment.taxilla_api + '/process/v1/files?fileUrl=' + encodeURIComponent(this.fileUrl);

        this.http.get(url, { responseType: 'arraybuffer' }).subscribe(async arrayBuffer => {
            try {
                const result = await mammoth.convertToHtml({ arrayBuffer });
                this.fileType = 'docx';
                this.textContent = this.sanitizer.bypassSecurityTrustHtml(result.value);
            } catch {
                this.textContent = 'Error: Could not process DOCX file';
            }
            this.utils.hideLoading();
        });
    };

    ngOnInit() {
        const ext = this.fileUrl.split('.').pop();
        switch (ext) {
            case 'pdf':
                this.processPDF();
                break;
            case 'txt':
            case 'csi':
                this.processText();
                break;
            case 'jpg':
            case 'png':
            case 'jpeg':
            case 'gif':
                this.processImage();
                break;
            case 'json':
                this.processJSON();
                break;
            case 'xml':
                this.processXML();
                break;
            case 'xls':
            case 'xlsx':
                this.processExcel();
                break;
            case 'doc':
                //this.processDoc();
                break;
            case 'docx':
                this.processDocx();
                break;
        }
    }

    ngOnDestroy(): void {
        URL.revokeObjectURL(this.safeUrl);
    }
}
